// PluginCreateModeKms.tsx
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  FormItem,
  Button,
  IconButton,
  Badge,
  Input,
  Textarea,
  Select,
  Icon,
  Tooltip,
  InputEditTable,
  Text,
  Heading,
} from '../../../components/base';
import { IKmsRule, IPluginPromptData } from '../../../libs/models/studio/PluginPromptList';
import KmsSearchDialog from './KmsSearchDialog';

interface IPluginCreateModeKmsProps {
  formData: IPluginPromptData;
  setFormData: React.Dispatch<React.SetStateAction<IPluginPromptData>>;
}

const KMS_MAX = 5;

const PluginCreateModeKms = forwardRef<{ validate: () => boolean }, IPluginCreateModeKmsProps>(
  ({ formData, setFormData }, ref) => {
    const [kmsRules, setKmsRules] = useState<IKmsRule[]>([]);
    const [searchTargetId, setSearchTargetId] = useState<number | null>(null);
    const [showError, setShowError] = useState(false);
    const [nameRemountKey, setNameRemountKey] = useState<Record<number, number>>({});

    const kmsActionOptions = [
      { value: 'Search', label: '조회' },
      { value: 'Write', label: '작성' },
      { value: 'SearchWrite', label: '조회 & 작성' },
    ];

    useImperativeHandle(ref, () => ({
      validate: () => {
        const valid = kmsRules.every((rule) => rule.url.trim() !== '');
        setShowError(!valid);
        return valid;
      },
    }));

    useEffect(() => {
      if (formData.kmsRules && Array.isArray(formData.kmsRules) && formData.kmsRules.length > 0) {
        const restored = formData.kmsRules.map((rule, index) => ({
          ...rule,
          frontId: index + 1,
          isExpanded: rule.isExpanded ?? true,
        }));
        setKmsRules(restored);
      }
    }, []);

    useEffect(() => {
      setFormData((data) => ({ ...data, kmsRules: kmsRules }));
    }, [kmsRules]);

    const handleAddKmsRule = () => {
      if (kmsRules.length >= KMS_MAX) return;
      const newId = kmsRules.length === 0 ? 1 : kmsRules[kmsRules.length - 1].frontId + 1;
      setKmsRules([
        ...kmsRules,
        {
          frontId: newId,
          displayName: '',
          condition: '',
          action: 'Search',
          url: '',
          title: '',
          description: '',
          isExpanded: true,
        },
      ]);
    };

    const handleDeleteKmsRule = (frontId: number) => {
      setKmsRules(kmsRules.filter((rule) => rule.frontId !== frontId));
    };

    const handleToggleKmsRule = (frontId: number) => {
      setKmsRules(
        kmsRules.map((rule) => (rule.frontId === frontId ? { ...rule, isExpanded: !rule.isExpanded } : rule)),
      );
    };

    const handleDisplayNameChange = (frontId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setKmsRules(kmsRules.map((rule) => (rule.frontId === frontId ? { ...rule, displayName: value } : rule)));
    };

    const handleConditionChange = (frontId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setKmsRules(kmsRules.map((rule) => (rule.frontId === frontId ? { ...rule, condition: value } : rule)));
    };

    const handleDescriptionChange = (frontId: number) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setKmsRules(kmsRules.map((rule) => (rule.frontId === frontId ? { ...rule, description: value } : rule)));
    };

    const handleActionChange = (frontId: number) => (selectedOption: string | number) => {
      const action = String(selectedOption) as IKmsRule['action'];
      setKmsRules(kmsRules.map((rule) => (rule.frontId === frontId ? { ...rule, action } : rule)));
    };

    // KMS 검색 팝업에서 페이지 선택 완료 → 해당 규칙 url + title 저장
    const handleKmsSelect = (node: { label: string; url: string }) => {
      if (searchTargetId == null) return;
      const target = kmsRules.find((rule) => rule.frontId === searchTargetId);
      const willAutoFill = !target?.displayName?.trim();
      setKmsRules(
        kmsRules.map((rule) =>
          rule.frontId === searchTargetId
            ? {
                ...rule,
                url: node.url,
                title: node.label,
                displayName: rule.displayName?.trim() ? rule.displayName : node.label,
              }
            : rule,
        ),
      );
      if (willAutoFill) {
        setNameRemountKey((prev) => ({ ...prev, [searchTargetId]: (prev[searchTargetId] ?? 0) + 1 }));
      }
    };

    const urlDisplay = (rule: IKmsRule) => {
      if (rule.title && rule.url) return `[${rule.title}] ${rule.url}`;
      return rule.url;
    };

    return (
      <>
        <FormItem
          label="KMS 목록"
          desc="KMS 스페이스/페이지는 최대 5개까지 등록할 수 있어요."
          info={
            <>
              <Badge size="large" accent="violet">
                {kmsRules.length}
              </Badge>
              <div className="dsx-side-right kms-list-info">
                <Button variant="normal" onClick={handleAddKmsRule} disabled={kmsRules.length >= KMS_MAX}>
                  추가하기
                </Button>
              </div>
            </>
          }
        >
          <div className="learning-list2">
            {kmsRules.map((rule, index) => {
              const urlInvalid = showError && rule.url.trim() === '';
              return (
                <div key={rule.frontId} className={`learning-item ${rule.isExpanded ? 'is-active' : ''}`}>
                  <div className="item-head">
                    <label htmlFor={`kms_name_${rule.frontId}`} className="dsx-Label">
                      <span className="label-badge">KMS {index + 1}</span>
                    </label>
                    <InputEditTable
                      key={`kms_name_${rule.frontId}_${nameRemountKey[rule.frontId] ?? 0}`}
                      id={`kms_name_${rule.frontId}`}
                      name="displayName"
                      maxLength={30}
                      value={rule.displayName ?? ''}
                      onChange={handleDisplayNameChange(rule.frontId)}
                    />
                    <div className="item-edit">
                      <IconButton
                        name="delete"
                        size="large"
                        onClick={() => {
                          handleDeleteKmsRule(rule.frontId);
                        }}
                      >
                        삭제
                      </IconButton>
                    </div>
                    <IconButton
                      name="chevronBottom"
                      size="large"
                      className="item-control"
                      aria-expanded={rule.isExpanded}
                      onClick={() => {
                        handleToggleKmsRule(rule.frontId);
                      }}
                    >
                      확장
                    </IconButton>
                  </div>

                  <div className="item-body">
                    <div className="item-data frm-panel">
                      <div className="frm-bind dsx-w-full kms-count-right">
                        <div className="dsx-title-bar kms-field-title">
                          <label htmlFor={`kms_condition_${rule.frontId}`} className="dsx-Label">
                            <Text as="span" size="body3" weight="semibold">
                              KMS 조회/작성 조건
                            </Text>
                          </label>
                          <div className="dsx-side-right">
                            <Tooltip
                              size="large"
                              position={['bottom', 'start']}
                              triggerEvent="click"
                              className="form-tooltip kms-guide-tooltip"
                              message={
                                <>
                                  <div className="tooltip-head">
                                    <Heading size="body1">KMS 등록 가이드</Heading>
                                  </div>
                                  <div className="tooltip-body">
                                    <Text size="body2" weight="medium">
                                      KMS 스페이스 혹은 페이지를
                                      <br />
                                      조회/작성할 조건을 작성해주세요.
                                    </Text>
                                    <div className="tooltip-point">Works AI Desktop 에 관련된 질문이 들어오면</div>
                                  </div>
                                </>
                              }
                            />
                          </div>
                        </div>
                        <Input
                          id={`kms_condition_${rule.frontId}`}
                          size="xlarge"
                          maxLength={100}
                          showCount
                          placeholder="조건을 입력해 주세요. (예시: Works AI Desktop에 관련된 질문이 들어오면)"
                          value={rule.condition}
                          onChange={handleConditionChange(rule.frontId)}
                          full
                        />
                      </div>

                      {/* KMS 페이지 (문서 선택) */}
                      <div className="frm-bind dsx-w-grow">
                        <div className="dsx-title-bar kms-field-title">
                          <label className="dsx-Label">
                            <Text as="span" size="body3" weight="semibold">
                              KMS 스페이스/페이지 선택
                            </Text>
                          </label>
                        </div>
                        <div
                          className="kms-url-field"
                          onClick={() => {
                            setSearchTargetId(rule.frontId);
                          }}
                        >
                          <Input
                            size="xlarge"
                            invalid={urlInvalid}
                            placeholder="KMS 스페이스 및 페이지를 선택해 주세요."
                            value={urlDisplay(rule)}
                            readOnly
                            slotAfter={<Icon name="search" />}
                            full
                          />
                        </div>
                      </div>

                      {/* 동작 */}
                      <div className="frm-bind kms-action-bind">
                        <div className="kms-action-inline">
                          <span className="kms-rule-conj">{rule.action === 'Search' ? '에서' : '에'}</span>
                          <Select
                            size="xlarge"
                            value={rule.action}
                            options={kmsActionOptions}
                            onChange={handleActionChange(rule.frontId)}
                          />
                        </div>
                      </div>

                      {/* 프롬프트 (상세 설명) */}
                      <div className="frm-bind dsx-w-full kms-count-right">
                        <div className="dsx-title-bar kms-field-title">
                          <label htmlFor={`kms_desc_${rule.frontId}`} className="dsx-Label">
                            <Text as="span" size="body3" weight="semibold">
                              프롬프트
                            </Text>
                          </label>
                          <div className="dsx-side-right">
                            <Tooltip
                              size="large"
                              position={['bottom', 'start']}
                              triggerEvent="click"
                              className="form-tooltip kms-guide-tooltip"
                              message={
                                <>
                                  <div className="tooltip-head">
                                    <Heading size="body1">KMS 등록 가이드</Heading>
                                  </div>
                                  <div className="tooltip-body">
                                    <Text size="body2" weight="medium">
                                      KMS 스페이스 혹은 페이지를
                                      <br />
                                      사용하여 답변할때 AI가 참고할 내용을 작성해주세요.
                                    </Text>
                                    <div className="tooltip-point">
                                      Works AI Desktop의 사용법과 관련된
                                      <br />
                                      FAQ의 Q/A를 함께 답변하세요.
                                    </div>
                                  </div>
                                </>
                              }
                            />
                          </div>
                        </div>
                        <Textarea
                          id={`kms_desc_${rule.frontId}`}
                          size="xlarge"
                          rows={3}
                          maxLength={2000}
                          showCount
                          placeholder="상세 설명을 입력해 주세요. (예시: Works AI Desktop의 사용법과 관련된 FAQ의 Q/A 를 함께 등록해주세요)"
                          value={rule.description ?? ''}
                          onChange={handleDescriptionChange(rule.frontId)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </FormItem>

        <KmsSearchDialog
          isOpen={searchTargetId != null}
          onClose={() => {
            setSearchTargetId(null);
          }}
          onSelect={handleKmsSelect}
        />
      </>
    );
  },
);

PluginCreateModeKms.displayName = 'PluginCreateModeKms';

export default PluginCreateModeKms;
